import { logException } from '@atlaskit/editor-common/monitoring';
import type { PMPluginFactoryParams, QuickInsertHandler } from '@atlaskit/editor-common/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Snippet } from '@atlassian/editor-snippets-provider/types';

import { insertSnippet } from './editor-commands/insert-snippet';
import { createReconcileSnippetCommand } from './editor-commands/reconcile-snippet';
import { snippetsPluginKey, createPlugin } from './pm-plugins/main';
import { isAdfNodeCompatibleWithSchema } from './pm-plugins/schemaCompatibility';
import type { SnippetsPlugin } from './snippetsPluginType';
import { getSnippetQuickInsertComponents } from './ui/quick-insert/getSnippetQuickInsertComponents';
import { getTemplateGalleryQuickInsertItem } from './ui/quick-insert/getTemplateGalleryQuickInsertItem';
import { mapToQuickInsertItem } from './ui/quick-insert/mapToQuickInsertItem';
import { useRegisterSnippets } from './ui/quick-insert/useRegisterSnippets';
import { registerTemplateGalleryEntryPoints } from './ui/registerTemplateGalleryEntryPoints';
import { getCreateSnippetToolbarComponents } from './ui/toolbar/getCreateSnippetToolbarComponents';

export const snippetsPlugin: SnippetsPlugin = ({ config, api }) => {
	const isConfluenceSnippetsPluginEnabled = isExperimentEnabled(
		'platform_editor_snippets_plugin_confluence',
	);
	registerTemplateGalleryEntryPoints({
		api,
		onCreateSnippet: config.onCreateSnippet,
		openTemplateGallery: config.openTemplateGallery,
	});
	if (config.onCreateSnippet) {
		api?.toolbar?.actions.registerComponents(
			getCreateSnippetToolbarComponents({ api, onCreateSnippet: config.onCreateSnippet }),
		);
	}

	if (!isConfluenceSnippetsPluginEnabled) {
		config.snippetsProvider
			.getSnippets()
			.then((snippets: Snippet[]) => {
				api?.core?.actions.execute(({ tr }) => {
					const compatibleSnippets = snippets.filter((snippet) =>
						isAdfNodeCompatibleWithSchema(snippet.body, tr.doc.type.schema),
					);

					if (!isExperimentEnabled('platform_editor_slash_command')) {
						if (!api?.quickInsert) {
							return null;
						}

						const quickInsertItems: QuickInsertHandler = (intl) => [
							...(config.openTemplateGallery
								? [getTemplateGalleryQuickInsertItem(config.openTemplateGallery, intl)]
								: []),
							...compatibleSnippets.map((snippet) => mapToQuickInsertItem(snippet, api, intl)),
						];
						return api.quickInsert.commands.addQuickInsertItem(quickInsertItems)({ tr });
					}

					const snippetQuickInsertComponents = getSnippetQuickInsertComponents({
						api,
						getItem: mapToQuickInsertItem,
						snippets: compatibleSnippets,
					});
					const orderedSnippetQuickInsertComponents = config.openTemplateGallery
						? snippetQuickInsertComponents.map((component) => ({
								...component,
								parents: component.parents?.map((parent) => ({
									...parent,
									rank: parent.rank + 1,
								})),
							}))
						: snippetQuickInsertComponents;

					api?.uiControlRegistry?.actions.register(orderedSnippetQuickInsertComponents);
					return tr;
				});
			})
			.catch((error) => {
				logException(error as Error, {
					location: 'editor-plugin-snippets/snippetsPlugin/getSnippets',
				});
			});
	}
	return {
		name: 'snippets',
		usePluginHook() {
			useRegisterSnippets({
				api,
				openTemplateGallery: config.openTemplateGallery,
				snippetsProvider: config.snippetsProvider,
			});
		},
		commands: {
			insertSnippet: isConfluenceSnippetsPluginEnabled ? insertSnippet(api, config) : undefined,
			reconcileSnippet: isConfluenceSnippetsPluginEnabled
				? createReconcileSnippetCommand(api)
				: undefined,
		},

		getSharedState(editorState) {
			if (!editorState) {
				return undefined;
			}
			return snippetsPluginKey.getState(editorState) ?? undefined;
		},

		pmPlugins() {
			return [
				{
					name: 'snippetsLoader',
					plugin: ({ portalProviderAPI }: PMPluginFactoryParams) => createPlugin(portalProviderAPI),
				},
			];
		},
	};
};
